/* eslint-disable no-underscore-dangle */
import { Alert, Button } from '@fountain/fountain-ui-components';
import { Grid } from '@material-ui/core';
import {
  DocumentSignatureStagesTemplateSetting,
  DocumentSigningStagesTemplateSetting,
  WorkflowDocumentSignatureStage,
  WorkflowDocumentSigningStage,
  WorkflowStageDetail,
} from 'api-clients/monolith';
import { useSimpleToggle } from 'hooks';
import produce from 'immer';
import React, { FC, PropsWithChildren, useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { AddItemRows } from 'containers/WorkflowEditor/components/AddItemRows';
import { ItemRow } from 'containers/WorkflowEditor/components/AddItemRows/ItemRow';
import { DocumentSigningRules } from 'containers/WorkflowEditor/components/Rules/DocumentSigningRules';
import { DocumentSigningRuleProps } from 'containers/WorkflowEditor/components/Rules/DocumentSigningRules/types';
import { StageSettingCard } from 'containers/WorkflowEditor/components/StageSettingCard';
import { SettingSwitch } from 'containers/WorkflowEditor/components/WorkflowEditorContent/SettingSwitch';
import { StageDetailPatchResponse } from 'containers/WorkflowEditor/contexts/stageContext';

import { AlternateDocuments } from './AlternateDocuments';
import { DEFAULT_DOCUMENT } from './constants';
import { DefaultDocuments } from './DefaultDocuments';
import { DocumentModal } from './DocumentModal';
import { collectGroupErrorMessages } from './errorUtils';
import { messages } from './messages';
import { NoTemplates } from './NoTemplates';
import { useStyles } from './styles';

const DocumentsStageSettingCard: FC<PropsWithChildren> = ({ children }) => {
  return (
    <StageSettingCard
      title={<FormattedMessage {...messages.documents} />}
      variant="default"
    >
      {children}
    </StageSettingCard>
  );
};

export interface DocumentsProps {
  stage: WorkflowDocumentSigningStage | WorkflowDocumentSignatureStage;
  setStage: React.Dispatch<React.SetStateAction<WorkflowStageDetail>>;
  setDocumentSigningRules: React.Dispatch<
    React.SetStateAction<DocumentSigningRuleProps>
  >;
  documentSigningRules: DocumentSigningRuleProps;
  updateStageResult: StageDetailPatchResponse;
}

export const Documents: VFC<DocumentsProps> = ({
  stage,
  setStage,
  setDocumentSigningRules,
  documentSigningRules,
  updateStageResult,
}) => {
  const intl = useIntl();
  const styles = useStyles();
  const { accountSlug } = useParams<{ accountSlug: string }>();

  const [selectedTemplateForModal, setSelectedTemplateForModal] = useState<
    | DocumentSigningStagesTemplateSetting
    | DocumentSignatureStagesTemplateSetting
  >();
  // The offer letter template the modal is editing, when it's that kind
  // (AXHE-4446). Kept separate from `selectedTemplateForModal` rather than
  // widened into it: that state is the legacy join shape, and the two open the
  // modal onto different persistence paths.
  const [
    selectedOfferLetterTemplateForModal,
    setSelectedOfferLetterTemplateForModal,
  ] = useState<number>();
  const {
    showContent: isModalOpen,
    on: openModal,
    off: closeModal,
  } = useSimpleToggle();

  const selectedTemplates =
    stage.additional_info.else_template_set_attributes?.template_settings_attributes?.filter(
      temp => !temp._destroy,
    ) ?? [];

  const availableTemplates =
    stage.additional_info.available_embedded_signature_templates?.filter(
      temp =>
        // Filter current selectedTemplates
        !selectedTemplates.some(
          selected =>
            selected.template_id ===
            (temp.document_signature_template_id ?? temp.id),
        ) ||
        // Add back selectedTemplate if editing
        selectedTemplateForModal?.template_id ===
          (temp.document_signature_template_id ?? temp.id),
    ) ?? [];

  const allTemplatesDeleted = Object.keys(selectedTemplates).length === 1;

  const maybeEmbeddedSignatureTemplateStages =
    stage.type === 'DocumentSigningStage'
      ? stage.additional_info.embedded_signature_templates_stages_attributes
      : undefined;
  const maybeAccountUsers =
    stage.type === 'DocumentSigningStage'
      ? stage.additional_info.account_users
      : undefined;

  // AXHE-4106: offer letter templates render in THIS list rather than the
  // separate card they used to have. An admin configuring an offer stage was
  // otherwise shown two document lists that looked unrelated, with no signal
  // which one to use.
  //
  // The two persistence paths stay distinct under the hood — legacy documents
  // live in `else_template_set_attributes`, offer letter templates in
  // `offer_letter_templates_stages_attributes` — so this is a presentation
  // merge, not a data merge. Nothing about an already-attached legacy document
  // changes when the offer-letter feature is switched on.
  const offerLetterAssignments =
    stage.type === 'DocumentSigningStage'
      ? (stage.additional_info.offer_letter_templates_stages_attributes ?? [])
      : [];
  const availableOfferLetterTemplates =
    stage.type === 'DocumentSigningStage'
      ? (stage.additional_info.available_offer_letter_templates ?? [])
      : [];

  const liveOfferLetterAssignments = offerLetterAssignments.filter(
    assignment => !assignment._destroy,
  );
  const assignedOfferLetterTemplates = liveOfferLetterAssignments
    .map(assignment => ({
      assignment,
      template: availableOfferLetterTemplates.find(
        candidate => candidate.id === assignment.offer_letter_template_id,
      ),
    }))
    .filter(
      (entry): entry is { assignment: typeof entry.assignment; template: NonNullable<typeof entry.template> } =>
        entry.template != null,
    );
  const assignedOfferLetterTemplateIds = assignedOfferLetterTemplates
    .map(entry => entry.template.id)
    .filter((id): id is number => id != null);
  // Attaching is DocumentModal's job — there is one "Add document" flow for
  // both kinds. Detaching stays here, because it's driven from the row.
  const handleDetachOfferLetterTemplate = (templateId: number) => {
    setStage(
      produce(stage, draftStage => {
        if (draftStage.type !== 'DocumentSigningStage') return;
        const attrs =
          draftStage.additional_info.offer_letter_templates_stages_attributes ?? [];
        const index = attrs.findIndex(
          assignment =>
            !assignment._destroy && assignment.offer_letter_template_id === templateId,
        );
        if (index === -1) return;

        // A persisted assignment needs `_destroy` to be removed server-side;
        // one added client-side this session has no id and can just be dropped.
        if (attrs[index].id != null) {
          attrs[index]._destroy = true;
        } else {
          attrs.splice(index, 1);
        }
        draftStage.additional_info.offer_letter_templates_stages_attributes = attrs;
      }),
    );
  };

  // AXHE-4446: an offer letter template whose roster names an employer
  // countersigner cannot be sent until this stage says WHO fills that role —
  // OfferLetters::SignerBuilder raises UnresolvableSignersError on a blank
  // mapping. Every attachment that predates the assignment UI has exactly that
  // gap, and nothing showed it: the stage saved clean and the offer failed later,
  // to a recruiter who had no way to fix it.
  //
  // So the row says it, phrased as the action, and the pencil beside it is where
  // that action happens. Not an error state: the stage is still saveable, by
  // design (see DocumentModal's `incompleteSigners`).
  const unassignedSignerWarning = (
    assignment: { signers_mapping?: Record<string, string> },
    template: { countersigner_roles?: Array<{ key?: string }> },
  ): string | undefined => {
    const unassigned = (template.countersigner_roles ?? []).filter(
      role => !assignment.signers_mapping?.[role.key ?? ''],
    );
    if (unassigned.length === 0) return undefined;

    return intl.formatMessage(messages.assignSigners, {
      count: unassigned.length,
    });
  };

  const handleAddDocument = () => {
    setSelectedTemplateForModal(DEFAULT_DOCUMENT);
    openModal();
  };

  const handleDeleteDocument = (id: string | number) => {
    setStage(
      produce(stage, draftStage => {
        // destroy the else_template_set if the last document is destroyed
        // NOTE: the corollary to this lives in <DocumentModal> `handleSubmit` where we
        // remove `_destroy` if the user repopulates the set
        if (
          allTemplatesDeleted &&
          draftStage.additional_info.else_template_set_attributes
        ) {
          draftStage.additional_info.else_template_set_attributes._destroy = true;
        }

        if (
          draftStage.additional_info.else_template_set_attributes
            ?.template_settings_attributes
        ) {
          const draftTemplateSettings =
            draftStage.additional_info.else_template_set_attributes
              .template_settings_attributes;

          const idx = draftTemplateSettings.findIndex(
            ts => ts.template_id === id,
          );

          draftTemplateSettings[idx]._destroy = true;
        }

        // Checking that it doesn't exist in alternate documents
        const isAlternateTemplateAlreadyUsed = Object.values(
          documentSigningRules,
        ).some(rule => {
          const templateSettings =
            rule.template_set_attributes.template_settings_attributes;

          return templateSettings?.some(
            setting => !setting._destroy && setting.template_id === id,
          );
        });

        if (
          draftStage.type === 'DocumentSigningStage' &&
          draftStage.additional_info
            .embedded_signature_templates_stages_attributes &&
          !isAlternateTemplateAlreadyUsed
        ) {
          const draftEmbeddedSignatureTemplatesStages =
            draftStage.additional_info
              .embedded_signature_templates_stages_attributes;

          const idx = draftEmbeddedSignatureTemplatesStages.findIndex(
            ests => ests.embedded_signature_template_id === id,
          );

          if (draftEmbeddedSignatureTemplatesStages[idx]) {
            draftEmbeddedSignatureTemplatesStages[idx]._destroy = true;
          }
        }
      }),
    );
  };

  const handleEditDocument =
    (
      template:
        | DocumentSignatureStagesTemplateSetting
        | DocumentSigningStagesTemplateSetting,
    ) =>
    () => {
      setSelectedTemplateForModal(template);
      openModal();
    };

  // AXHE-4446: the pencil on an offer letter row opens the document modal, the
  // same as a legacy row's does. It used to navigate to the offer letter template
  // EDITOR, which conflated two different acts: this row is a stage attachment —
  // who signs here — while the template is shared by every stage using it.
  // Editing the template from the pencil meant an admin aiming to change one
  // stage changed the document everywhere. The modal still links out to the
  // editor for the cases where that IS the intent.
  const handleEditOfferLetterTemplate = (templateId: number) => () => {
    setSelectedOfferLetterTemplateForModal(templateId);
    openModal();
  };

  const handleCloseDocumentModal = () => {
    setSelectedTemplateForModal(undefined);
    setSelectedOfferLetterTemplateForModal(undefined);
    closeModal();
  };

  // Hire sends the key only where the landing job reads it.
  const sendDocumentsOnLanding =
    stage.type === 'DocumentSigningStage'
      ? stage.additional_info.send_documents_on_landing
      : undefined;
  const defersLandingMessage =
    stage.type === 'DocumentSigningStage' &&
    stage.additional_info.defers_landing_message;

  // Both of these count offer letter templates as well as legacy documents, and
  // that is load-bearing now that the two kinds share one list (AXHE-4106).
  //
  // Keyed on the legacy lists alone, an offer-letter-ONLY stage — no legacy
  // documents attached and none available — took the early return below and
  // rendered `NoTemplates`, so its attached offer letter was invisible AND
  // unremovable: the detach action lives on a row that never rendered. The same
  // omission made `isEmpty` report the list as empty while it held offer
  // templates, which is what draws the "no documents" error state
  // (fountain-reef, PR #2996).
  //
  // The stage can also hold a .docx template the list cannot draw: a
  // sub-account stage assigned to a parent account's template has no entry in
  // `available_offer_letter_templates` while offer-letter-template-inheritance
  // is off, so `assignedOfferLetterTemplates` drops it. The landing job still
  // sends it. `defers_landing_message` reads the join itself, and counts here
  // only with the switch's key, so a stage without the key reads as it did
  // before (AXHE-4226).
  const hasOfferLetterContent =
    assignedOfferLetterTemplates.length > 0 ||
    availableOfferLetterTemplates.length > 0 ||
    (sendDocumentsOnLanding !== undefined && defersLandingMessage);

  // Offer letter templates the picker would actually offer. `availableOfferLetter
  // Templates` is the whole account list including ones already attached here, so
  // it can't stand in for "there is still something to add" — DocumentModal drops
  // the assigned ids from its own options, and this mirrors that filter off the
  // same `assignedOfferLetterTemplateIds` it is handed, so the two cannot drift.
  const addableOfferLetterTemplates = availableOfferLetterTemplates.filter(
    template =>
      template.id == null || !assignedOfferLetterTemplateIds.includes(template.id),
  );
  const listIsEmpty =
    selectedTemplates.length === 0 && assignedOfferLetterTemplates.length === 0;

  // The switch needs a stage that can hold a .docx template: the legacy
  // documents already send on landing, so on their own it would change nothing.
  const showSendDocumentsOnLanding =
    sendDocumentsOnLanding !== undefined && hasOfferLetterContent;
  const sendsDocumentsOnLanding =
    showSendDocumentsOnLanding && sendDocumentsOnLanding !== false;

  const errors =
    (updateStageResult.isError && updateStageResult.error?.errors) || undefined;

  // Collect all group_error messages for else_template_set_attributes, and
  // the provider errors, which no field on this card can highlight
  const groupErrorMessages = collectGroupErrorMessages(errors, key => {
    return (
      (key.startsWith('else_template_set_attributes') &&
        key.endsWith('.group_error')) ||
      key === 'else_template_set.template_settings.template' ||
      key === 'document_signature_provider'
    );
  });

  // Rendered on the empty card too: a provider conflict refuses the save
  // whether or not the account has templates to list.
  const errorsAlert = groupErrorMessages.length > 0 && (
    <Alert fullWidth type="danger" className={styles.errorsAlert}>
      {/* TODO: this needs to be translated! */}
      <div>
        {groupErrorMessages.map(message => (
          <div key={message}>{message}</div>
        ))}
      </div>
    </Alert>
  );

  if (
    availableTemplates.length === 0 &&
    selectedTemplates.length === 0 &&
    !hasOfferLetterContent
  ) {
    return (
      <DocumentsStageSettingCard>
        {errorsAlert}
        <NoTemplates />
      </DocumentsStageSettingCard>
    );
  }

  return (
    <DocumentsStageSettingCard>
      {errorsAlert}
      {/* Reuses the predicate that already guards the NoTemplates early return
          above rather than inventing a parallel notion of "this list can hold an
          offer letter": a stage that cannot hold one must read exactly as it did
          before AXHE-4106 put the two kinds in one list (AXHE-4226). */}
      <DefaultDocuments
        className={styles.marginBottom2}
        hasOfferLetterTemplates={hasOfferLetterContent}
        sendsDocumentsOnLanding={sendsDocumentsOnLanding}
      />
      <AddItemRows
        className={styles.addDefaultDocumentsSection}
        emptyContentDescription={intl.formatMessage(messages.noDocuments)}
        isEmpty={listIsEmpty}
        addButton={
          <Button
            type="secondary"
            size="small"
            autoWidth
            className={styles.addButton}
            onClick={handleAddDocument}
            // Disabled only when there is genuinely nothing left to attach of
            // EITHER kind and something is already attached. Before AXHE-4106
            // folded offer letter templates into this same picker, both operands
            // could look at legacy documents alone; afterwards, a stage holding
            // every available legacy template disabled Add permanently, leaving
            // an admin no way to attach an offer letter template to it at all.
            disabled={
              availableTemplates.length === 0 &&
              addableOfferLetterTemplates.length === 0 &&
              (selectedTemplates.length > 0 ||
                assignedOfferLetterTemplates.length > 0)
            }
          >
            <FormattedMessage {...messages.addDocument} />
          </Button>
        }
        errorText={intl.formatMessage(messages.noDocumentsError)}
      >
        <Grid container direction="column">
          {selectedTemplates.map(template => {
            const {
              embedded_signature_template_title:
                templateTitle = intl.formatMessage(
                  messages.templateTitleMissing,
                ),
              template_id: templateId = uuid(),
              document_signature_template_id: documentSignatureTemplateId,
            } = template;
            return (
              <Grid className={styles.row} key={templateId}>
                <ItemRow
                  title={templateTitle}
                  id={documentSignatureTemplateId ?? templateId ?? 0}
                  key={templateId}
                  onDelete={handleDeleteDocument}
                  onEdit={handleEditDocument(template)}
                  confirmDeleteModalTitle={intl.formatMessage(
                    messages.removeDocument,
                  )}
                  confirmDeleteModalButton={intl.formatMessage(messages.remove)}
                  confirmDeleteModalDescription={intl.formatMessage(
                    messages.removeDocumentConfirmationText,
                    {
                      title: templateTitle,
                    },
                  )}
                />
              </Grid>
            );
          })}
          {/* Offer letter templates, in the same list as the legacy documents
              above (AXHE-4106). Which kind a row is is no longer written on it
              (AXHE-4446) — the section description says it once for the whole
              list, and per-row it was noise. */}
          {assignedOfferLetterTemplates.map(({ assignment, template }) => (
            <Grid className={styles.row} key={`offer-letter-${template.id}`}>
              <ItemRow
                // Name alone while the template is active (AXHE-4446). Only the
                // inactive case still earns a suffix, because it describes a
                // broken assignment the recruiter has to clear rather than a
                // standing fact about offer letter templates in general.
                title={
                  template.active === false
                    ? `${template.title ?? ''} · ${intl.formatMessage(
                        messages.offerLetterInactiveBadge,
                      )}`
                    : (template.title ?? '')
                }
                warning={unassignedSignerWarning(assignment, template)}
                id={template.id ?? 0}
                onEdit={handleEditOfferLetterTemplate(template.id ?? 0)}
                onDelete={() => handleDetachOfferLetterTemplate(template.id ?? 0)}
                confirmDeleteModalTitle={intl.formatMessage(messages.removeDocument)}
                confirmDeleteModalButton={intl.formatMessage(messages.remove)}
                confirmDeleteModalDescription={intl.formatMessage(
                  messages.removeDocumentConfirmationText,
                  { title: template.title ?? '' },
                )}
              />
            </Grid>
          ))}
        </Grid>
      </AddItemRows>
      <div>
        <AlternateDocuments className={styles.alernateDocuments} />
        <DocumentSigningRules
          stage={stage}
          setStage={setStage}
          setDocumentSigningRules={setDocumentSigningRules}
          documentSigningRules={documentSigningRules}
          errors={errors}
        />
      </div>
      {showSendDocumentsOnLanding && (
        <div className={styles.sendDocumentsOnLanding}>
          <SettingSwitch
            dataKey="send_documents_on_landing"
            checked={sendsDocumentsOnLanding}
            onChange={() => {
              setStage(
                produce(stage, draftStage => {
                  if (draftStage.type !== 'DocumentSigningStage') return;
                  draftStage.additional_info.send_documents_on_landing =
                    !sendsDocumentsOnLanding;
                }),
              );
            }}
            label={<FormattedMessage {...messages.sendDocumentsOnLanding} />}
            description={
              <FormattedMessage
                {...messages.sendDocumentsOnLandingDescription}
              />
            }
          />
        </div>
      )}
      {isModalOpen && (
        <DocumentModal
          onClose={handleCloseDocumentModal}
          availableTemplates={availableTemplates}
          existingTemplate={selectedTemplateForModal}
          embeddedSignatureTemplateStages={maybeEmbeddedSignatureTemplateStages}
          accountUsers={maybeAccountUsers}
          // One "Add document" flow for both kinds (AXHE-4106).
          availableOfferLetterTemplates={availableOfferLetterTemplates}
          assignedOfferLetterTemplateIds={[...assignedOfferLetterTemplateIds]}
          // AXHE-4446: the modal assigns an employer signer per role and writes
          // it to the join row, so it needs the live assignments to load from —
          // and the account slug to link out to the template editor.
          existingOfferLetterTemplateId={selectedOfferLetterTemplateForModal}
          offerLetterTemplatesStages={liveOfferLetterAssignments}
          accountSlug={accountSlug}
          setStage={setStage}
          key={
            selectedTemplateForModal?.embedded_signature_template_title ??
            selectedOfferLetterTemplateForModal ??
            uuid()
          }
        />
      )}
    </DocumentsStageSettingCard>
  );
};
